'use client';

import { cx } from 'class-variance-authority';
import { useEffect, useRef } from 'react';
import type { ClassValue } from 'class-variance-authority/types';
import { useStateObject } from '~/utils/react';
import { useTimeout } from '~/components/hooks/useTimeout';
const ONE_FPS = 1000 / 60;
const DELAY = ONE_FPS / 2;
const CHART_COLORS = {
  /** 'text-turqoise-500' */
  turqoise: 'rgb(55 180 162)',
  /** 'text-yellow-500' */
  yellow: 'rgb(249 177 57)',
  teriary: 'rgb(235 88 72)'
};
const BG_COLOR = 'rgba(255,255,255, 0.8)';
export const PercentageCircleChart: React.FC<{
  percentage: number | null;
  color: keyof typeof CHART_COLORS;
  className?: ClassValue;
}> = props => {
  const outerEl = useRef<HTMLDivElement>(null);
  const innerEl = useRef<HTMLSpanElement>(null);
  const currentPercentage = useStateObject(props.percentage === null ? null : 0);
  const color = props.percentage !== null && props.percentage > 100 ? CHART_COLORS.teriary : CHART_COLORS[props.color];
  useEffect(() => {
    if (props.percentage === currentPercentage.value) return;
    currentPercentage.set(0);
  }, [props.percentage]);
  useTimeout(() => {
    if (currentPercentage.value === null || props.percentage === null || currentPercentage.value >= props.percentage) return;
    currentPercentage.set(prev => Math.min((prev ?? 0) + 2, props.percentage ?? 0, 100));
  }, DELAY, [currentPercentage.value]);
  const chartOffset = ((outerEl.current?.clientWidth ?? 0) - (innerEl.current?.clientWidth ?? 0)) / 2;
  return <div ref={outerEl} className={cx('relative flex items-center justify-center rounded-full bg-brown-50', 'h-48 w-48', props.className)} style={{
    background: `conic-gradient(${color} ${currentPercentage.value ?? 0}%, ${BG_COLOR} 0%)`,
    color
  }}>
            <span ref={innerEl} className="bizz-bg relative z-1 flex h-24 w-24 items-center justify-center rounded-full text-3xl font-extrabold leading-none" style={{
      // 90px
      width: '5.625rem',
      height: '5.625rem'
    }}>
                <span aria-hidden className="absolute bottom-full rounded-full bg-brown-50" style={{
        maxWidth: chartOffset,
        maxHeight: chartOffset,
        width: chartOffset,
        height: chartOffset
      }} />
                {props.percentage === null ? '-' : `${Math.round(props.percentage)}%`}
                {currentPercentage.value !== null && <span aria-hidden className="absolute rounded-full" style={{
        maxWidth: chartOffset,
        maxHeight: chartOffset,
        width: chartOffset,
        height: chartOffset,
        backgroundColor: color,
        transform: [`rotate(${Math.min(currentPercentage.value, 100) * 3.6}deg)`, `translateY(calc(50% - ${outerEl.current?.clientWidth ?? 0}px/2))`].join(' ')
      }} />}
            </span>
        </div>;
};